import { Injectable } from '@angular/core';
import { catchError, EMPTY, map, Observable, of, ReplaySubject, shareReplay, Subject, Subscription } from 'rxjs';
import { promiseWithCatch } from 'src/app/common/app.helpers';
import ProjectLinks from 'src/app/common/project.links';
import { FormTypes } from 'src/app/forms/forms.enum';
import { FieldAttribute, FormConstructorContent } from 'src/app/interfaces/form-and-field.interface';
import { TableCellOnActionEvent } from 'src/app/interfaces/tables.interfaces';
import {
  AccountInvolvements,
  CommunityInviteOrDeclineRequest,
  CreateProjectRequest,
  EmptyResponse,
  PaginatableListOfBasicUsers,
  Post,
  Project,
  ProjectCommunity,
  ProjectsBasic,
  RequestCountersBasic,
  SocialGroupCountersBasic,
  UpdateProjectRequest,
  ValidityPeriod,
} from 'src/app/swagger-api';
import { UserTableCreators } from 'src/app/views/table-users-list/adapters/table-creator.enum';
import { TableCreatorFactory } from 'src/app/views/table-users-list/adapters/table-creator.method';
import { withReplaceBadRequest } from '../../services/api-error';

import { loadingHttpContext } from 'src/app/common/interceptors/tokens';
import { ItemsListContent } from 'src/app/interfaces/views.interface';
import { MaybeNull } from 'src/app/utils';
import {
  SocialGroupMembers,
  SocialGroupType,
} from '../search/search-component/invite-to-social-group/social-group.interface';
import { SocialInitiative } from '../shared/social-initiative/social-initiative.abstract';
import { projectFormMetadata } from './manage-project.metadata';

@Injectable()
export class ManageProjectService extends SocialInitiative<Project> {
  private projectInfoSubject$: ReplaySubject<Project> = new ReplaySubject(1);

  private projectTabsCountersChanges$: Subject<SocialGroupCountersBasic> = new Subject();
  private projectsInvolvementCountersChanges$: Subject<RequestCountersBasic> = new Subject();

  public readonly SOCIAL_GROUP_TYPE: SocialGroupType = 'Project';
  public readonly SOCIAL_ENTITY_ROUTE_LINK: string = ProjectLinks.LINK_PROJECTS_VIEW;

  public get entityChanged$(): Observable<Project> {
    return this.projectInfoSubject$.asObservable();
  }

  public get projectsInvolvementCountersChanges(): Observable<RequestCountersBasic> {
    return this.projectsInvolvementCountersChanges$.pipe(shareReplay(1));
  }

  public get projectTabsCountersChanges(): Observable<SocialGroupCountersBasic> {
    return this.projectTabsCountersChanges$.asObservable();
  }

  public get isExpired$(): Observable<boolean> {
    return this.projectInfoSubject$.pipe(
      map((project: Project) => this.checkIsProjectExpired(project.validity_period.end_date)),
    );
  }

  public get content$(): Observable<MaybeNull<ItemsListContent<Post>>> {
    return this.projectInfoSubject$.pipe(
      map((project: Project) =>
        !project.permissions.add_post
          ? null
          : {
              createItemInfo: {
                data: {
                  project_id: {
                    entity_id: project.entity_id,
                    title: project.title,
                  },
                },
                returnUrl: `${ProjectLinks.LINK_PROJECTS_VIEW}/${project.entity_id}`,
                createLink: ProjectLinks.LINK_POSTS_CREATE,
                createItemTitle: 'BTN_CREATE_POST',
              },
            },
      ),
    );
  }

  public fetchRequestProjectCounters(projectId: string): void {
    const subscription: Subscription = this.apiService
      .restSocialProjectsCounters(projectId)
      .pipe(catchError(() => EMPTY))
      .subscribe((counters: SocialGroupCountersBasic) => {
        this.projectTabsCountersChanges$.next(counters);
        subscription.unsubscribe();
      });
  }

  public fetchRequestCounters(projectId: string): void {
    const subscription: Subscription = this.apiService
      .restSocialProjectMembersInvolvementCounters(projectId)
      .pipe(catchError(() => EMPTY))
      .subscribe((counters: RequestCountersBasic) => {
        this.projectsInvolvementCountersChanges$.next(counters);
        subscription.unsubscribe();
      });
  }

  public fetchAttributeMetadata(data?: Project | object, isManageData = false, isNewAttributeWithData = false): void {
    this.preprocessAttrsData(data, isManageData);

    const mockedAttrs: FormConstructorContent = {
      formAttribute: this.formsAttributeService.getFormAttribute(FormTypes.PROJECT),
      fieldAttributes: this.fieldsService.convertAttributesMetadata(
        projectFormMetadata,
        FormTypes.PROJECT,
        data ? (data as Project).permissions : null,
      ),
      data: isManageData || isNewAttributeWithData ? data : {},
    };

    this.formAttrChanges$.next(this.getPostProcessedAttrs(mockedAttrs, isManageData, isNewAttributeWithData));
  }

  public onConfirmRequestOrInvite(event: TableCellOnActionEvent): Observable<EmptyResponse> {
    return this.apiService
      .restSocialProjectMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Approved },
        event.entityId,
        event.actorId,
      )
      .pipe(withReplaceBadRequest({}));
  }

  public onRejectRequestOrInvite(event: TableCellOnActionEvent): Observable<EmptyResponse> {
    return this.apiService
      .restSocialProjectMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY));
  }

  public onRemoveInvite(event: TableCellOnActionEvent): Observable<EmptyResponse> {
    return this.apiService
      .restSocialProjectMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY));
  }

  public fetchUsersRequestsForEntity(
    entityId: string,
    offset: number,
    limit: number,
    sorting: string,
    allowManaging: boolean,
  ): void {
    this.fetchEntityUsersRequests(
      this.apiService.restSocialProjectMembersIndexRequests(entityId, offset, limit, sorting, loadingHttpContext()),
      entityId,
      allowManaging,
    );
  }

  public fetchUsersInvitationsForEntity(
    entityId: string,
    offset: number,
    limit: number,
    sorting: string,
    allowManaging: boolean,
  ): void {
    this.fetchEntityInvites(
      this.apiService.restSocialProjectMembersIndexPending(entityId, offset, limit, sorting, loadingHttpContext()),
      entityId,
      allowManaging,
    );
  }

  public createEntity(entity: object): void {
    this.preprocessProjectData(entity, true);
    this.apiService
      .restSocialProjectsPost(entity as CreateProjectRequest)
      .pipe(catchError(() => EMPTY))
      .subscribe((project: Project) => {
        promiseWithCatch(this.router.navigateByUrl(`${ProjectLinks.LINK_PROJECTS_VIEW}/${project.entity_id}`));
      });
  }

  public updateEntity(entity: UpdateProjectRequest, entityId: string): void {
    this.preprocessProjectData(entity);
    this.apiService
      .restSocialProjectsPut(entity, entityId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        promiseWithCatch(this.router.navigateByUrl(`${ProjectLinks.LINK_PROJECTS_VIEW}/${entityId}`));
      });
  }

  public removeProject(projectId: string): void {
    this.apiService
      .restSocialProjectsDelete(projectId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        promiseWithCatch(
          this.router.navigateByUrl(
            this.sessionService.isAdminSession() ? ProjectLinks.LINK_DEFAULT : ProjectLinks.LINK_PROJECTS_ALL,
          ),
        );
      });
  }

  public fetchEntityDetails(entityId: string): void {
    this.apiService
      .restSocialProjectsGet(entityId)
      .pipe(catchError(() => EMPTY))
      .subscribe((project: Project) => {
        this.entity = project;
        this.projectInfoSubject$.next(project);
      });
  }

  public fetchEntityMemberIds(socialGroupId: string): Observable<SocialGroupMembers> {
    return this.apiService.restSocialProjectsAccountInvolvements(socialGroupId).pipe(
      map((involvements: AccountInvolvements): SocialGroupMembers => {
        return {
          socialGroupId: involvements.social_group_id,
          memberIds: new Set(involvements.participant_account_ids),
          pendingMemberIds: new Set(involvements.pending_invitation_account_ids),
          pendingJoinRequests: new Set(involvements.pending_request_account_ids),
        };
      }),
      // On error, just return empty sets
      catchError(() => {
        return of({
          socialGroupId: socialGroupId,
          memberIds: new Set<string>(),
          pendingMemberIds: new Set<string>(),
          pendingJoinRequests: new Set<string>(),
        });
      }),
    );
  }

  public fetchEntityMembers(projectId: string, offset?: number, limit?: number): void {
    this.apiService
      .restSocialProjectMembersIndex(projectId, offset, limit, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((paginatableUsers: PaginatableListOfBasicUsers) => {
        this.members = paginatableUsers.items;
        this.membersChanges$.next(paginatableUsers);
        this.membersTableDataChanges$.next(
          TableCreatorFactory.createTableConstructorData(UserTableCreators.MANAGE_USERS, {
            users: paginatableUsers.items,
            entity: this.entity,
          }),
        );
      });
  }

  public removeMember(userId: string, entityId: string, offset: number, limit: number): void {
    this.apiService
      .restSocialProjectMembersDelete(entityId, userId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.fetchEntityData(entityId);
        this.fetchEntityMembers(entityId, offset, limit);
      });
  }

  public joinEntity(projectId: string, offset: number, limit: number): void {
    this.apiService
      .restSocialProjectMembersPost({ user_id: this.sessionService.userId }, projectId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.fetchEntityData(projectId);
        this.fetchEntityMembers(projectId, offset, limit);
      });
  }

  public inviteToEntity(userId: string, entityId: string): void {
    this.apiService
      .restSocialProjectMembersPost({ user_id: userId }, entityId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.fetchEntityData(entityId);
      });
  }

  public leaveEntity(entityId: string, offset: number, limit: number): void {
    this.apiService
      .restSocialProjectMembersDelete(entityId, this.sessionService.userId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.fetchEntityData(entityId);
        this.fetchEntityMembers(entityId, offset, limit);
      });
  }

  public navigateToInviteToEntitySearchPage(id: string): void {
    this.navigateToInvite(`/invite-to-project/${id}`);
  }

  public getRelatedProjects(id: string, limit?: number, offset?: number): Observable<ProjectsBasic> {
    return this.apiService.restSocialRelatedProjectsIndex(id, offset, limit);
  }

  public checkIsProjectExpired(endDate: string): boolean {
    const nowWithoutTimestamp: number = new Date().setHours(0, 0, 0, 0);

    return new Date(nowWithoutTimestamp) > new Date(endDate);
  }

  /**
   * @description Adds additional property to data and remove redundant in case
   *              isManageData flag value is truthy.
   * @param { Project } data
   * @param { boolean } isManageData
   */
  private preprocessAttrsData(data: Project | object, isManageData: boolean): void {
    if (isManageData && data['validity_period']) {
      data['community_id'] = data['community'] as ProjectCommunity;
      data['start_date'] = (data['validity_period'] as ValidityPeriod)['start_date'];
      data['end_date'] = (data['validity_period'] as ValidityPeriod)['end_date'];

      delete data['community'];
      delete data['validity_period'];
    }
  }

  /**
   * @description Removes accept_terms property in case isCreateData flag value is truthy.
   *              Sets linked_organizations property value.
   * @param { object } entity
   * @param { boolean } isCreateData
   */
  private preprocessProjectData(entity: object, isCreateData?: boolean): void {
    if (isCreateData) {
      if (entity['accept_terms']) {
        delete entity['accept_terms'];
      }
    }

    entity['validity_period'] = {
      start_date: entity['start_date'] as string,
      end_date: entity['end_date'] as string,
    };
    delete entity['start_date'];
    delete entity['end_date'];
  }

  /**
   * @description Removes unneeded property from attributes for create entity flow
   *              (in case isManageData flag value is truthy).
   * @param { FormConstructorContent } attrs
   * @param { boolean } isManageData
   * @param { boolean } isNewAttributeWithData
   * @returns { FormConstructorContent }
   */
  private getPostProcessedAttrs(
    attrs: FormConstructorContent,
    isManageData: boolean,
    isNewAttributeWithData = false,
  ): FormConstructorContent {
    if (isManageData) {
      attrs.fieldAttributes = attrs.fieldAttributes.filter(
        (item: FieldAttribute) => item.backendAttribute.name !== 'accept_terms',
      );
    }

    attrs.fieldAttributes = this.setDisableField(attrs, isNewAttributeWithData);

    return attrs;
  }

  /**
   * @description Sets the read_only value of the input field depending on the
   *              passed parameter to avoid user editing.
   * @param { FormConstructorContent } attrs
   * @param { boolean } isDisabled
   * @returns { FieldAttribute[] }
   */
  private setDisableField(attrs: FormConstructorContent, isDisabled: boolean): FieldAttribute[] {
    attrs.fieldAttributes = attrs.fieldAttributes.map((item: FieldAttribute) => {
      if (item.backendAttribute.name === 'community_id') {
        item.backendAttribute.read_only = isDisabled;
      }
      return item;
    });
    return attrs.fieldAttributes;
  }
}
